import { type AuthorModel } from '@/shared/api'
import { TABLE_PAGE_SIZE_OPTIONS } from '@/shared/lib/query'
import { TableWrapper } from '@/shared/ui'
import { Table, type TableProps } from 'antd'
import { useUnit } from 'effector-react'

import { type AuthorsSortState } from '../../lib/authors-query-model'
import {
  $authorsTablePage,
  $authorsTablePageSize,
  $authorsTableSort,
  authorsTablePageChanged,
  authorsTablePageReset,
  authorsTablePageSizeChanged,
  authorsTableQuery,
  authorsTableSortChanged,
  authorsTableSortReset,
} from '../../model/authors-table.model'
import { useAuthorsTableColumns } from './useAuthorsTableColumns'

interface AuthorsTableProps {
  onEdit: (authorId: string) => void
}
export const AuthorsTable = ({ onEdit }: AuthorsTableProps) => {
  const { data, pending } = useUnit(authorsTableQuery)

  const sort = useUnit($authorsTableSort)
  const changeSort = useUnit(authorsTableSortChanged)
  const resetSort = useUnit(authorsTableSortReset)

  const page = useUnit($authorsTablePage)
  const changePage = useUnit(authorsTablePageChanged)
  const resetPage = useUnit(authorsTablePageReset)
  const pageSize = useUnit($authorsTablePageSize)
  const changePageSize = useUnit(authorsTablePageSizeChanged)

  const tableColumns: TableProps<AuthorModel>['columns'] =
    useAuthorsTableColumns({ onEdit, sort })

  if (!data?.items || !data.items.length) {
    return null
  }

  const onTableChange: TableProps<AuthorModel>['onChange'] = (
    pagination,
    _filters,
    sorter,
  ) => {
    // Sort
    if (!Array.isArray(sorter)) {
      const field = sorter.field as string | undefined
      const order = sorter.order as 'ascend' | 'descend' | undefined

      if (field && order) {
        const sortOrder = order === 'ascend' ? 'asc' : 'desc'

        if (sort.field !== field || sort.order !== sortOrder) {
          changeSort({
            field: field as AuthorsSortState['field'],
            order: sortOrder,
          })
          resetPage()
        }
      } else if (sort.field !== null || sort.order !== null) {
        resetSort()
        resetPage()
      }
    }
    // Pagination
    if (pagination.current && pagination.pageSize) {
      if (pagination.current !== page) {
        changePage(pagination.current)
      }
      if (pagination.pageSize !== pageSize) {
        changePageSize(pagination.pageSize)
        resetPage()
      }
    }
  }

  return (
    <TableWrapper>
      <Table
        sticky
        showSorterTooltip={false}
        rowKey={(record) => record.id}
        bordered
        columns={tableColumns}
        dataSource={data.items}
        loading={pending}
        onChange={onTableChange}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data.totalItems,
          showSizeChanger: true,
          pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
          locale: { items_per_page: 'Исполнителей на странице' },
        }}
      />
    </TableWrapper>
  )
}
